import React, { useState, useEffect, useCallback } from 'react';

import { apiPatch, apiGet, apiPut } from '@src/services/api/helpers';

import { styled } from '@mui/material/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Badge from '@mui/material/Badge';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CancelIcon from '@mui/icons-material/Cancel';

import { useCreatePath } from 'react-admin';

import { Permissions } from '@src/global/permissions';
import { classToResource } from '@src/utils/model-helpers';

const StyledMain = styled(Grid)(({ theme }) => ({
    position: 'absolute',
    left: 'calc(100% - 415px)',
    color: theme.palette.text.primary,
    background: theme.palette.background.paper,
    top: '48px',
    width: '360px',
    maxHeight: '400px',
    overflowY: 'scroll',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    boxShadow: theme.palette.mode === 'dark' ? '0px 4px 12px rgba(0, 0, 0, 0.5)' : '-1px 4px 12px 5px rgba(176, 176, 176, 0.56)',
    zIndex: theme.zIndex.appBar + 1,
}));

const StyledMenu = styled('a')({
    cursor: 'pointer',
    paddingRight: '16px',
    paddingLeft: '16px',
});

const StyledMenuBadge = styled(Badge)({
    cursor: 'pointer',
    marginRight: '16px',
});

const StyledTitle = styled(Typography)(({ theme }) => ({
    display: 'block',
    alignItems: 'center',
    padding: '12px',
    color: theme.palette.primary.main,
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '16px',
    minHeight: '50px',
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
    'padding': '12px 0',
    '&:nth-child(odd)': {
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#eee',
    },
}));

const StyledListItemIcon = styled(ListItemIcon)({
    cursor: 'pointer',
    minWidth: '40px',
    textAlign: 'center',
    padding: '0 10px',
});

const StyledListItemText = styled(ListItemText)({
    'cursor': 'pointer',
    'width': '100%',
    '&:hover': {
        textDecoration: 'underline',
    },
});

const AdminNotifications = () => {
    const createPath = useCreatePath();
    const [isLoading, setIsLoading] = useState(true);
    const [adminNotifications, setAdminNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        apiGet('/admin_notifications').then((response) => {
            const { data } = response;

            setAdminNotifications(data.admin_notifications);
        }).catch((error) => {
            console.error(error);
        });
    }, []);

    const handleClick = useCallback(() => {
        setIsOpen(!isOpen);
        apiGet('/admin_notifications').then((response) => {
            const { data } = response;

            setAdminNotifications(data.admin_notifications);
            setIsLoading(false);
        }).catch((error) => {
            setIsLoading(false);
            console.error(error);
        });
    }, [setIsLoading, setIsOpen, isOpen]);

    const handleClickAway = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    const handleReviewClick = useCallback((id) => {
        apiPatch(`/admin_notifications/${id}/review`).then(() => {
            setAdminNotifications(adminNotifications.filter((an) => an.id !== id));
        }).catch((error) => {
            console.error(error);
        });
    }, [adminNotifications]);

    const handleReviewAllClick = useCallback(() => {
        apiPut('/admin_notifications/review_all').then(() => {
            setAdminNotifications([]);
        }).catch((error) => {
            console.error(error);
        });
    }, []);

    const handleItemClick = useCallback((an) => {
        const resource = classToResource(an.record_type);
        if (resource === 'social_sharings' || resource === 'field_management_news' || resource === 'articles') {
            return null;
        }

        if (Permissions(resource, 'update') === false) {
            return null;
        }

        const linkToResource = createPath({ resource, id: an.resource.id, type: 'edit' });
        window.location.href = linkToResource;
    }, [createPath]);

    return (
        <>
            <StyledMenu
                onClick={handleClick}
            >
                <StyledMenuBadge badgeContent={adminNotifications.length} overlap="rectangular" color="primary">
                    <NotificationsNoneIcon />
                </StyledMenuBadge>
                NOTIFICATIONS
            </StyledMenu>
            {
                isOpen ?
                    <ClickAwayListener onClickAway={handleClickAway}>
                        <StyledMain container spacing={2}>
                            <Grid size={{ xs: 12, md: 12 }}>
                                <StyledTitle variant="h6">
                                    <NotificationsNoneIcon style={{ display: 'inline-block', float: 'left', marginRight: '12px' }} fontSize="large" />
                                    <div style={{ display: 'inline-block', float: 'left' }}>
                                        NOTIFICATIONS<br />
                                        <small style={{ fontSize: '11px', fontWeight: '400' }}>You will see updates and modifications</small>
                                    </div>
                                    {
                                        adminNotifications.length > 0 &&
                                        <a
                                            style={{ display: 'inline-block', float: 'right', cursor: 'pointer' }}
                                            onClick={handleReviewAllClick}
                                        >
                                            <img
                                                src="/img/broom.svg"
                                                alt="Clear All"
                                                title="Clear All"
                                                style={{ width: '32px', height: '32px' }}
                                            />
                                        </a>
                                    }
                                </StyledTitle>
                                <div>
                                    {isLoading ? <p style={{ padding: '12px' }}>Loading...</p> : null}
                                    {
                                        !isLoading && (
                                            <List>
                                                {
                                                    adminNotifications.map((an) => {
                                                        return (
                                                            <StyledListItem key={an.id}>
                                                                <StyledListItemIcon>
                                                                    <CancelIcon
                                                                        fontSize="small"
                                                                        onClick={() => handleReviewClick(an.id)}
                                                                    />
                                                                </StyledListItemIcon>
                                                                <StyledListItemText
                                                                    primary={an.title}
                                                                    secondary={an.note ? an.note : null}
                                                                    primaryTypographyProps={{
                                                                        style: {
                                                                            fontWeight: 'bold',
                                                                            fontSize: '12px',
                                                                            paddingRight: '10px',
                                                                            lineHeight: '14px',
                                                                        },
                                                                    }}
                                                                    secondaryTypographyProps={{
                                                                        style: {
                                                                            fontSize: '0.75rem',
                                                                        },
                                                                    }}
                                                                    onClick={() => handleItemClick(an)}
                                                                />
                                                            </StyledListItem>
                                                        );
                                                    })
                                                }
                                            </List>
                                        )
                                    }
                                </div>
                            </Grid>
                        </StyledMain>
                    </ClickAwayListener> :
                    null
            }
        </>
    );
};

AdminNotifications.propTypes = {};

export default AdminNotifications;
